import type { SeriesView } from './api'

/**
 * How long a series may go unobserved and still be treated as ongoing.
 *
 * Measured on 2026-08-22: successive maps of a Bo3 opened their lobbies 70 and 84 minutes apart,
 * including the interval where a decider was set up. Three hours is comfortably outside that and
 * comfortably inside "nobody is playing this any more".
 *
 * It is the same reasoning as `LiveMatchService.RunningWindow`, arrived at from the other side — a
 * series with nothing observed for hours is not between maps, whatever the shape of its rows says.
 */
const STALE_AFTER_SECONDS = 3 * 60 * 60

/**
 * Is this series still being played?
 *
 * Not the same as "a map is live". Between games of a Bo3 nothing is in progress — the draft for
 * the next map has not begun — so a series that has just finished game one reports no live map and
 * was dropping out of the live band entirely, even though everyone watching knows another game is
 * coming.
 *
 * A series is in play if a map is being played, or if one is still to come. All three sections read
 * this one predicate: when they disagreed, a match appeared twice or vanished.
 *
 * "Has started and is not decided" is NOT enough, and the difference is a map that has been played
 * whose result has not reached us. OpenDota indexes well behind real time, so the last map of a
 * series sits `awaiting` for up to a sync cycle after everybody watching has seen it end — and
 * during that window the old rule pinned a finished series under "In play", which is the same
 * conflation as rendering `awaiting` as "Not started". Nothing is being played and nothing is to
 * come; we are waiting on a result, so it belongs with the finished matches, where its own row says
 * "Result pending".
 *
 * **"A map is still to come" is a forecast, and it decays.** That was the remaining hole and it is
 * the one that reached the site. On 2026-08-22 a lobby was abandoned mid-draft: neither Valve nor
 * OpenDota ever recorded a match for it, so its row stayed `awaiting` for ever, map three stayed
 * `pending` behind it, and the series was never decided. Every clause above held, none of them
 * involved a clock, and BoomBoys vs Team Spirit sat under "In play" for **eight hours** after the
 * last thing anybody played.
 *
 * So the two halves are held to different standards, which is the whole shape of this fix. *A map
 * is live* is an observation and is believed outright. *Another map is coming* is an inference from
 * a series that is unfinished, and it is only worth anything while the series has recently shown
 * signs of life.
 *
 * Deliberately NOT fixed by deleting the offending row. `PhantomMatchCleanupService` already
 * refuses to, and says why: a real match whose result OpenDota is slow to index looks exactly like
 * one that never happened, so a stale row carrying a prediction is left alone rather than deleting
 * genuine predictions to tidy up a display. This is that display, corrected where it belongs.
 *
 * @param clock Seconds since the epoch, wrapped in an object, and the wrapper is the whole point.
 * The clock has to be injectable — the assertion here is about *how old* a series is, and a
 * predicate that reads the clock itself can only be tested against the calendar, which is exactly
 * how a backend test came to expire on this same day. But a bare `now: number` second parameter is
 * a loaded gun next to `Array.prototype.filter`, which calls back with `(element, index, array)`:
 * `series.filter(isInPlay)` then passes the INDEX as the time, so the first series is judged
 * against the epoch, `now - lastSeen` is hugely negative, and nothing is ever stale. That shipped.
 * It typechecked, because an index is a number and so was this. An object cannot be an index, so
 * the same mistake is now a compile error rather than a silent one.
 */
export function isInPlay(series: SeriesView, clock?: { nowSeconds: number }): boolean {
  // Falls back rather than trusting what it was handed, because the type system does not have our
  // back here: this project does not run `strict`, so parameter bivariance lets
  // `series.filter(isInPlay)` compile and hand the INDEX to `clock`. An object parameter documents
  // the intent and stops nothing.
  //
  // Left unguarded the failure is silent and total: `(0).nowSeconds` is undefined, `undefined -
  // lastSeen` is NaN, `NaN > bound` is false, so every series reads as fresh and the staleness rule
  // does nothing whatsoever. Guarded, the same mistake degrades to "use the real clock", which is
  // what the caller wanted anyway.
  const now =
    typeof clock?.nowSeconds === 'number' && Number.isFinite(clock.nowSeconds)
      ? clock.nowSeconds
      : Date.now() / 1000
  if (series.isLive) {
    return true
  }

  // A map awaiting its result has been played, so the series has certainly started — but that is
  // the question of whether it is OVER, not of whether it is running.
  const started = series.maps.some(
    (map) => map.teamAWon !== null || map.isLive || map.status === 'awaiting',
  )

  const running = series.maps.some((map) => map.isLive || map.status === 'live')
  const stillToCome = series.maps.some((map) => map.status === 'pending')

  // The freshest thing we know of this series. Map start times are lobby times, so this moves as
  // each game is announced; the series' own kick-off carries a series no map has been seen of yet.
  const lastSeen = Math.max(
    series.startTime,
    ...series.maps.map((map) => map.startTime ?? 0),
  )

  const stale = now - lastSeen > STALE_AFTER_SECONDS

  return started && !series.decided && (running || (stillToCome && !stale))
}
